/* eslint-disable jsx-a11y/anchor-is-valid */
import LoadError from 'components/LoadError';
import React, { useState, useEffect } from 'react';
import { callAPI, openPdfTab, userErrorMessage, showErrorToast, showSuccessToast, copyToClipboard, folioEditClosed, formatFolioClosedDate, thaiYearShort } from 'helpers';
import _ from 'lodash';
import moment from 'moment';
import config from 'config';
import cn from 'classnames';

// Components
import { IconFolio, IconFolioStar, IconFolioStarOutline, IconChevronDown, IconDownload, IconDuplicate, IconCopyLink } from 'components/icons';
import { Link, useNavigate } from 'react-router-dom';

// Hooks
import { useSelector } from 'react-redux';
import useCoursesR1 from 'hooks/useCoursesR1';

// สถานะพอร์ต (#9) — เลือกได้ค่าเดียว; submitted = ล็อกแก้ (เปลี่ยนสถานะออกเพื่อกลับมาแก้), unused = ดันลงล่างสุด
// export ให้ SectionFolioSearch ใช้แปลง status → label บนการ์ดสาขาที่มีแฟ้มแล้ว
export const FOLIO_STATUSES = [
  { value: 'building', label: 'กำลังสร้างพอร์ต' },
  { value: 'ready', label: 'พร้อมส่ง' },
  { value: 'submitted', label: 'ยื่นสมัครแล้ว' },
  { value: 'unused', label: 'ไม่ใช้แล้ว' },
];

const SectionFolios = () => {
  const navigate = useNavigate();
  const student = useSelector((state) => _.get(state, 'app.user') || {});
  const user = useSelector((state) => _.get(state, 'app.folioUser') || {});
  const citizen_id_verified = Boolean(_.get(student, 'citizen_id')) || Boolean(user.citizen_id_verified);

  const { coursesR1, courseMapR1: courseMap, loadFailed: coursesFailed, retry: retryCourses } = useCoursesR1();
  // reducer แปะแถว 999 นำหน้าเสมอ → มีแถว = โหลดไฟล์แล้ว (ก่อนโหลด courseMap ว่าง ทุกใบจะดูเหมือน "หาย")
  // > 1: แถว 999 อย่างเดียว = S3 คืน [] (ไฟล์ว่าง/พัง) ไม่ใช่โหลดสำเร็จ — ไม่งั้นทุกใบกลายเป็น "ถูกถอน"
  const coursesLoaded = coursesR1.length > 1;

  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [folios, setFolios] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const fetchFolios = async () => {
      try {
        setLoading(true);
        const { result } = await callAPI({
          url: '/folios/tcasfolios'
        });
        if (cancelled) return;
        setLoading(false);
        setFolios(result);
      } catch (error) {
        console.error(new Error(`Error while trying to lookup by citizenId: ${userErrorMessage(error)}`));
        if (cancelled) return;
        setLoadFailed(error && error.statusCode === 401 ? 'unauthorized' : true);   // 401 = session หมด: บอกให้เข้าสู่ระบบ ไม่ใช่ "โหลดใหม่" วน
        setLoading(false);
      }
    }

    fetchFolios();
    return () => { cancelled = true; };
  }, [])

  const removeIfExists = (value, text = '') => {
    const v = value || '';   // null จาก Program doc ไม่ใช่ undefined → default param ไม่ช่วย (เดิม null.startsWith พังทั้ง dashboard)
    if (!v.startsWith(text)) return `${text}${v}`;
    return v;
  }
  // session หมดอายุระหว่างเปิด dashboard ค้าง — ทุก action ให้บอกตรง ๆ แทน "ดาวน์โหลดไม่สำเร็จ แฟ้มอาจถูกแก้…" ที่ผิดสาเหตุ
  const sessionExpired = (error) => {
    if (_.get(error, 'statusCode') !== 401) return false;
    showErrorToast('การเข้าสู่ระบบหมดอายุ กรุณาเข้าสู่ระบบใหม่แล้วลองอีกครั้ง');
    return true;
  };

  const [activeFolio, setActiveFolio] = useState();
  const [modalType, setModalType] = useState(false);
  const _onModalOpen = (folio, modalType) => {
    setActiveFolio(folio);
    setModalType(modalType);
  }
  const _onModalClose = () => {
    setActiveFolio();
    setModalType();
  }

  const [downloading, setDownloading] = useState(false);
  const _onDownload = async (folio) => {
    if (downloading) return;
    const pdfTab = openPdfTab();   // ต้องเปิดแบบ sync ก่อน await — ดูเหตุผล bfcache/presign ใน helpers
    try {
      setDownloading(true);
      // อ่านอย่างเดียว — ไม่เรียก create-pdf จาก dashboard แล้ว (เดิมข้าม validation ของ builder:
      // regen แฟ้ม stale ได้ PDF พังเงียบ ๆ) ถ้าแฟ้มถูกแก้หลังโหลดหน้า server ตอบ 404
      const { pdf } = await callAPI({
        genPdf: true,
        method: 'POST',
        url: `/folios/${folio._id}/get-pdf`,
        body: { action: 'download' }   // ให้ server insertLog แยก download ออกจาก link_view
      });

      pdfTab.navigate(pdf);

      setDownloading(false);
      _onModalClose();
    } catch (error) {
      pdfTab.close();
      console.error(new Error(`Error while trying to download folio pdf: ${userErrorMessage(error)}`));
      // ข้อความ "แฟ้มอาจถูกแก้ไขหลังสร้าง PDF" เฉพาะ 404 (stale) — 5xx/เน็ตหลุดบอกอย่างนั้นจะพาเด็กไป gen ใหม่โดยไม่จำเป็น
      if (!sessionExpired(error)) showErrorToast(userErrorMessage(error, error && error.statusCode === 404 ? 'ดาวน์โหลดไม่สำเร็จ — แฟ้มอาจถูกแก้ไขหลังสร้าง PDF กรุณาเปิดแฟ้มแล้วกด “สร้างไฟล์ PDF” ใหม่' : 'ดาวน์โหลดไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'));
      setDownloading(false);
    }
  }

  // ปักดาว (favorite) — ดันการ์ดขึ้นบนสุด; optimistic update แล้วค่อย sync, พลาดค่อย revert
  const [favBusy, setFavBusy] = useState(null);   // folioId ที่กำลัง save
  const _onToggleFavorite = async (folio) => {
    if (favBusy) return;
    const favorite = !folio.favorite;
    setFavBusy(folio._id);
    setFolios((list) => list.map((f) => (f._id === folio._id ? { ...f, favorite } : f)));
    try {
      await callAPI({
        method: 'PUT',
        url: `/folios/tcasfolios/${folio._id}/favorite`,
        body: { favorite }
      });
    } catch (error) {
      console.error(new Error(`Error while trying to toggle folio favorite: ${userErrorMessage(error)}`));
      setFolios((list) => list.map((f) => (f._id === folio._id ? { ...f, favorite: !favorite } : f)));
      if (!sessionExpired(error)) showErrorToast(userErrorMessage(error));
    } finally {
      setFavBusy(null);
    }
  };

  // กลุ่มไม่ใช้แล้ว — default ยุบ ไม่ persist (reload กลับมายุบ)
  const [showUnused, setShowUnused] = useState(false);

  // สถานะพอร์ต — optimistic แบบเดียวกับปักดาว
  const [statusBusy, setStatusBusy] = useState(null);
  const _onSetStatus = async (folio, status) => {
    if (statusBusy || (folio.status || 'building') === status) return;
    const prev = folio.status;
    setStatusBusy(folio._id);
    setFolios((list) => list.map((f) => (f._id === folio._id ? { ...f, status } : f)));
    // เปลี่ยนเป็นไม่ใช้แล้วตอนกลุ่มยุบอยู่ = การ์ดหายวับต่อหน้า — กางให้เห็นว่าย้ายลงกลุ่มล่าง
    if (status === 'unused') setShowUnused(true);
    try {
      await callAPI({
        method: 'PUT',
        url: `/folios/tcasfolios/${folio._id}/status`,
        body: { status }
      });
    } catch (error) {
      console.error(new Error(`Error while trying to set folio status: ${userErrorMessage(error)}`));
      setFolios((list) => list.map((f) => (f._id === folio._id ? { ...f, status: prev } : f)));
      if (!sessionExpired(error)) showErrorToast(userErrorMessage(error));
    } finally {
      setStatusBusy(null);
    }
  };

  const [copying, setCopying] = useState(false);
  const _onLinkCopy = async (folio) => {
    if (copying) return;
    const url = `${config.folioHost}view-folio/${folio._id}`;
    setCopying(true);

    // อ่านอย่างเดียว — ยิง get-pdf ก่อน copy เพื่อ (1) ยืนยันว่าลิงก์ resolve ได้
    // (แฟ้มไม่ถูกแก้หลังโหลด dashboard) (2) insertLog การคัดลอกครบทุกครั้ง
    // ลิงก์ view-folio/{folio._id} ถาวรต่อแฟ้ม — ตาย 404 ชั่วคราวช่วง stale แล้วกลับมาใช้ได้เองหลัง gen ใหม่
    try {
      await callAPI({
        genPdf: true,
        method: 'POST',
        url: `/folios/${folio._id}/get-pdf`,
        body: { action: 'copy_link' }
      });
    } catch (error) {
      // ล้มที่ get-pdf = ลิงก์ยังใช้ไม่ได้จริง — ข้อความนี้ใช้เฉพาะกรณีนี้เท่านั้น
      console.error(new Error(`Error while trying to resolve folio PDF before copy: ${userErrorMessage(error)}`));
      if (!sessionExpired(error)) showErrorToast(userErrorMessage(error, error && error.statusCode === 404 ? 'คัดลอกลิงก์ไม่สำเร็จ — แฟ้มอาจถูกแก้ไขหลังสร้าง PDF กรุณาเปิดแฟ้มแล้วกด “สร้างไฟล์ PDF” ใหม่' : 'คัดลอกลิงก์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'));
      setCopying(false);
      return;
    }

    // clipboard แยก try — สำเร็จก็ไม่มีอะไรเปลี่ยนบนจอ ต้องมี toast ยืนยัน
    // (เคส 08-31: เด็กกดซ้ำ 3–5 ครั้งใน 1–2 วินาที ทั้งที่ copy_link ผ่านทุกครั้ง เพราะไม่รู้ว่าติดแล้ว)
    try {
      await copyToClipboard(url);
      showSuccessToast('คัดลอกลิงก์แฟ้มแล้ว');   // pill สั้น — เงื่อนไขผู้เปิดลิงก์อธิบายไว้ใน modal ก่อนกดแล้ว
    } catch (error) {
      // เบราว์เซอร์ปฏิเสธ clipboard (Safari/in-app browser ตัด user gesture หลัง await) — โชว์ลิงก์ให้คัดลอกเอง
      console.error(new Error(`Error while trying to write folio URL to clipboard: ${userErrorMessage(error)}`));
      showErrorToast(`เบราว์เซอร์ไม่อนุญาตให้คัดลอกอัตโนมัติ — กดค้าง/ลากเลือกลิงก์นี้เพื่อคัดลอกเอง: ${url}`);
    }
    setCopying(false);
    _onModalClose();
  }

  useEffect(() => {
    const classNames = document.getElementById('root').className.split(' ');
    if (loading) {
      document.getElementById('root').className = _.uniq(classNames.concat(['is-loading'])).join(' ');
    } else {
      document.getElementById('root').className = classNames.filter((name) => name !== 'is-loading').join(' ');
    }
  }, [loading]);

  useEffect(() => {
    const classNames = document.getElementById('root').className.split(' ');
    if (downloading) {
      document.getElementById('root').className = _.uniq(classNames.concat(['is-loading'])).join(' ');
    } else {
      document.getElementById('root').className = classNames.filter((name) => name !== 'is-loading').join(' ');
    }
  }, [downloading]);

  if (loadFailed) {
    return (
      <div className="t-box -folio">
        <h2 className="title">แฟ้มสะสมผลงาน</h2>
        {loadFailed === 'unauthorized'
          ? <LoadError className="" message="การเข้าสู่ระบบหมดอายุ กรุณาเข้าสู่ระบบใหม่" />
          : <LoadError className="" />}
      </div>
    );
  }

  // แยกกลุ่ม — ไม่ใช้แล้วอยู่หลังปุ่มกาง/ยุบท้ายกลุ่ม active (คงลำดับ API); active เรียงปักดาวบนสุด (sortBy stable)
  const unusedFolios = (folios || []).filter((folio) => folio.status === 'unused');
  const activeFolios = _.sortBy((folios || []).filter((folio) => folio.status !== 'unused'),
    (folio) => (folio.favorite ? 0 : 1));

  // ดาวมุมขวาบน — ตำแหน่ง/ฟีลเดียวกับถังขยะ (.i-del absolute right:10px top:10px) ของการ์ดรอบ 3 (FavoriteItem)
  const favoriteStar = (folio) => (
    <button
      type="button"
      className="btn-blank absolute top-1.5 right-1.5 p-1 transition-opacity"
      disabled={favBusy === folio._id}
      aria-pressed={Boolean(folio.favorite)}
      title={folio.favorite ? 'เอาออกจากรายการโปรด' : 'ปักดาวไว้บนสุด'}
      onClick={() => _onToggleFavorite(folio)}>
      {folio.favorite
        ? <IconFolioStar width="22" height="22" />
        : <IconFolioStarOutline width="22" height="22" className="text-[#999999] opacity-70 hover:opacity-100 hover:text-folio" />}
    </button>
  );

  // แถวสถานะพอร์ต — แสดงทุกแฟ้มโดยไม่ผูกกับการสร้าง PDF; ชิป 4 ตัวเลือกได้ค่าเดียว
  // และเลื่อนแนวนอนได้บนจอแคบ (แนว swipe)
  const statusChips = (folio, cloneAllowed = false) => {
    const current = folio.status || 'building';
    return (
      <div
        className="px-4 md:px-6 pb-3 w-full gap-3"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center' }}>
        <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto" role="radiogroup" aria-label="สถานะพอร์ต">
          <span className="text-xs opacity-60 shrink-0 pr-1">สถานะ</span>
          {FOLIO_STATUSES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={current === value}
              disabled={statusBusy === folio._id}
              className={cn(
                'text-xs rounded-full border px-2.5 py-1 whitespace-nowrap shrink-0 transition-colors cursor-pointer',
                current === value ? 'bg-folio border-folio text-white font-bold' : 'border-folio/40 text-folio bg-white/60'
              )}
              onClick={() => _onSetStatus(folio, value)}>
              {label}
            </button>
          ))}
        </div>
        {cloneAllowed && (
          <button
            type="button"
            className="group s-flex shrink-0 cursor-pointer gap-1.5 rounded-md border border-folio/40 bg-folio_bg px-3 py-1.5 text-sm font-medium text-folio transition-colors hover:border-folio hover:bg-folio/10"
            style={{ justifySelf: 'end' }}
            onClick={() => navigate(`/profile/folios/${folio._id}`, { state: { openClonePicker: true } })}>
            <IconDuplicate width="18" height="18" />ทำสำเนาไปสาขาอื่น
          </button>
        )}
      </div>
    );
  };

  // การ์ดหนึ่งใบ — ใช้ทั้งกลุ่ม active และกลุ่มไม่ใช้แล้ว (hr คั่นเฉพาะภายในกลุ่มของตัวเอง)
  const renderFolio = (folio, index, list) => {
    const courseKey = `${folio.program_id || 0}_${folio.major_id || 0}_${folio.project_id || 0}`;
    const course = _.get(courseMap, courseKey) || {};
    // หลักสูตรหายจาก courses_round1.json หลังสร้างแฟ้ม (สถาบันถอน/ลบโครงการ — เคส รามาฯ ฉุกเฉินการแพทย์ 2026-08-17)
    // เดิม render ป้ายเปล่า "วิทยาเขต/คณะ/สาขา/หลักสูตร" + logo แตก ดูเหมือน bug — แฟ้มยังอยู่ใน DB
    // ห้ามซ่อน: เนื้อหายังทำสำเนาไปหลักสูตรอื่นได้ (ปุ่มอยู่ใน builder) — FE มีแค่รหัส ชื่อหลักสูตรไม่ได้เก็บใน folio
    // server (GET list) แนบ course_deleted จาก ProgramProject จริง — เชื่อก่อน JSON: JSON กรอง is_folio_hidden ออกและ regen รายชั่วโมง
    // หลักสูตรที่ยังอยู่แต่ถูกซ่อน/ยังไม่โผล่ ≠ ถูกถอน · ไม่มี flag (BE เก่า) → heuristic เดิม (หายจาก JSON = ถอน)
    const notInJson = coursesLoaded && folio.university_id !== '999' && _.isEmpty(course);
    const courseDeleted = folio.course_deleted === true || (folio.course_deleted == null && notInJson);
    const courseMissing = courseDeleted || notInJson;   // ซ่อน/ยังไม่โผล่ใน JSON ก็ใช้การ์ดเดียวกัน (ต่างแค่ server รู้ว่าถอนจริง)
    const majorName = _.get(course, 'major_name_th') || '';
    const projectName = _.get(course, 'project_name_th') || '';
    const lastMoment = moment(_.get(folio, 'updated_at') || _.get(folio, 'created_at'));
    const lastTimestamp = `${lastMoment.format('D MMM')} ${thaiYearShort(lastMoment)} ${lastMoment.format('เวลา HH.mm.ss น.')}`;   // ปี พ.ศ. บวกที่ตัวเลข (29 ก.พ. ไม่โดนปัด)
    // ปิดรับสมัคร → ห้ามแก้ (per หลักสูตร) — ลิงก์เข้า builder คงไว้ (เปิดโหมดดูอย่างเดียว, 09-14); PDF/คัดลอกลิงก์ยังใช้ได้
    // (แฟ้มที่แก้ค้างไว้โดยไม่ gen ก่อนปิดรับ → เปิดแฟ้มแล้วกด "สร้างไฟล์ PDF" ได้จากเนื้อหาที่บันทึกไว้ — 2026-10-06)
    const editClosed = folioEditClosed(course);
    // สถานะยื่นสมัครแล้ว = ล็อกแก้เหมือนกัน แต่ปลดได้ด้วยการเปลี่ยนสถานะ
    const submitted = (folio.status || 'building') === 'submitted';
    // ไม่ใช้แล้ว = การ์ดจางทั้งใบ + ซ่อนปุ่มดาว (ค่า favorite ใน DB คงไว้ แค่ไม่แสดง)
    // และล็อกแก้ด้วย — เผลอ save จะทำ PDF/ลิงก์ที่แชร์ไปแล้วกลายเป็นฉบับก่อนแก้ (stale)
    const unused = (folio.status || 'building') === 'unused';
    // ล็อกทั้งสามแบบยังเข้า builder ได้ (โหมดดูอย่างเดียว) + ทำสำเนาได้ — เหลือแค่ป้ายบอกเหตุผลบนการ์ด
    // ดาวน์โหลด/คัดลอกลิงก์ = อ่านอย่างเดียว (get-pdf) — ปลดล็อกเมื่อเคยสร้าง PDF สำเร็จ (pdf_path)
    // 2026-09-08: save ไม่ล้าง pdf_path แล้ว (ลิงก์ที่ส่งมหาลัยไปต้องไม่ตาย) — แฟ้มที่แก้หลัง gen ล่าสุด
    // มาเป็นธง pdf_stale: ปุ่มยังใช้ได้ แต่ป้ายเตือนว่าไฟล์เป็นฉบับก่อนแก้ ต้องกด “สร้างไฟล์ PDF” ใน builder
    // (ทางเดียวที่ gen — มี validation หน้าเกิน/รูป/เอกสารครบ) ถ้าต้องการอัปเดต
    const hasPdf = Boolean(folio.pdf_path);
    // pdf_stale (แก้หลัง gen ล่าสุด) ไม่เตือนบนการ์ดแล้ว — ตัดสินใจ 09-14: ปุ่มดาวน์โหลด/คัดลอกลิงก์ให้ฉบับล่าสุดที่ gen ไว้
    // (แถบเตือน + ปุ่ม "สร้างไฟล์ PDF" อยู่ใน builder ซึ่งเป็นที่เดียวที่ gen ใหม่ได้)
    const noPdfHint = folio.pdf_updated_at
      ? 'ยังไม่มีไฟล์ PDF ของแฟ้มนี้ — เปิดแฟ้มแล้วกด “สร้างไฟล์ PDF” ก่อน'
      : 'ยังไม่เคยสร้างไฟล์ PDF — เปิดแฟ้มแล้วกด “สร้างไฟล์ PDF” ก่อน';

    // ยังไม่ได้ไฟล์หลักสูตร — ขึ้นบรรทัดสถานะแทนป้ายเปล่า (วูบเดียว ไม่ต้องมี link/ปุ่ม)
    if (!coursesLoaded && folio.university_id !== '999') {
      return (
        <React.Fragment key={folio._id}>
          <div className={cn('relative border mb-4 border-t-[3px] bg-folio_bg rounded-md border-folio _heading leading-tight', { 'opacity-40': unused })}>
            <div className="p-4 md:p-6">
              {coursesFailed ? (
                <div className="text-red-600">
                  โหลดข้อมูลหลักสูตรไม่สำเร็จ{' '}
                  <button type="button" className="underline" onClick={retryCourses}>ลองใหม่</button>
                </div>
              ) : <div className="opacity-50">กำลังโหลดข้อมูลหลักสูตร…</div>}
              <div className="text-sm pt-2 opacity-50">อัปเดตล่าสุดเมื่อ {lastTimestamp}</div>
            </div>
          </div>
          {(index < list.length - 1) && <hr />}
        </React.Fragment>
      );
    }

    if (courseMissing) {
      // ไม่มีลิงก์เข้า builder — หลักสูตรไม่มีให้สมัครแล้ว ให้เด็กกดไม่ได้ (เหลือ PDF/คัดลอกลิงก์ที่มีอยู่ + ชิปสถานะ)
      const names = _.get(folio, 'course.university_name_th') ? folio.course : null;
      return (
        <React.Fragment key={folio._id}>
          <div className={cn('relative border mb-4 border-t-[3px] bg-folio_bg rounded-md border-folio _heading leading-tight', { 'opacity-40': unused })} data-testid="folio-card-missing-course">
            {!unused && favoriteStar(folio)}
            {/* ชื่อจาก DB (server แนบ folio.course จาก doc ที่ลบแล้ว) — ไม่มี = server เก่า/หา program ไม่เจอ → โชว์รหัสแทน */}
            {names && (
              <div className="float-right pt-9 pr-4 md:pt-10 md:pr-6">
                <img
                  src={`https://assets.mytcas.com/i/logo/${folio.university_id}.png`}
                  alt="University Logo"
                  width={44}
                  height={44}
                  className="size-11 sm:size-16 md:size-20"
                />
              </div>
            )}
            <div className="p-4 md:p-6">
              {/* ถอน (server course_deleted) และซ่อน/ยังไม่โผล่ใน JSON ใช้ข้อความเดียวกัน — ตัดสินใจ 2026-09-02 */}
              <strong className="sm:text-xl text-[#A13333]">ไม่พบหลักสูตรนี้ในรายการรับสมัครแล้ว</strong>
              {names ? (
                <div className="text-base sm:text-lg pt-1">
                  <div className="font-bold">{names.university_name_th}</div>
                  <div>{removeIfExists(names.campus_name_th, 'วิทยาเขต')}</div>
                  <div>{removeIfExists(names.faculty_name_th, 'คณะ')}</div>
                  <div>{removeIfExists(names.field_name_th, 'สาขา')}</div>
                  <div>{removeIfExists(names.program_name_th, 'หลักสูตร')}</div>
                  {names.major_name_th && names.major_name_th !== '0' ? (
                    <div>{removeIfExists(names.major_name_th, 'วิชาเอก')}</div>
                  ) : null}
                  {names.project_name_th ? (
                    <div className="text-sm pt-2">{removeIfExists(names.project_name_th, 'โครงการ')}</div>
                  ) : null}
                </div>
              ) : (
                // ไม่มีชื่อให้โชว์ (BE เก่า/doc หายจริง) — เหลือรหัสไว้ให้ระบุแฟ้มได้
                <div className="text-base sm:text-lg pt-1">รหัสหลักสูตร {courseKey}</div>
              )}
              <div className="text-sm pt-2 opacity-50">อัปเดตล่าสุดเมื่อ {lastTimestamp}</div>
            </div>
            {statusChips(folio)}
            {Boolean(citizen_id_verified) && (
              <>
                <div className="bg-folio/50 h-[1px]" />
                <div className="px-4 md:px-6 py-2 flex items-center gap-2 text-base font-bold justify-between">
                  <a
                    className={cn('s-flex gap-1 text-folio', hasPdf ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed')}
                    role="button" tabIndex={0} aria-disabled={!hasPdf}
                    title={hasPdf ? undefined : noPdfHint}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                    onClick={() => (hasPdf ? _onModalOpen(folio, 'download') : showErrorToast(noPdfHint))}>
                    <IconDownload />
                    ดาวน์โหลด PDF
                  </a>
                  <button
                    className={cn('btn-blank font-bold text-base s-flex gap-1 text-folio', { 'opacity-40 cursor-not-allowed': !hasPdf })}
                    aria-disabled={!hasPdf}
                    title={hasPdf ? undefined : noPdfHint}
                    onClick={() => (hasPdf ? _onModalOpen(folio, 'copy') : showErrorToast(noPdfHint))}>
                    <IconCopyLink />คัดลอกลิงก์
                  </button>
                </div>
              </>
            )}
          </div>
          {(index < list.length - 1) && <hr />}
        </React.Fragment>
      );
    }

    if (folio.university_id === '999') {
      return (
        <React.Fragment key={folio._id}>
          <div className={cn('relative border mb-4 border-t-[3px] bg-folio_bg rounded-md border-folio _heading leading-tight', { 'opacity-40': unused })}>
            {!unused && favoriteStar(folio)}
            <div className="p-4 md:p-6">
              <Link to={`/profile/folios/${folio._id}`}>
                <strong className="sm:text-xl">ยังไม่ระบุสาขาวิชา ➔</strong>
              </Link>
              <div className="text-sm pt-2 opacity-50">อัปเดตล่าสุดเมื่อ {lastTimestamp}</div>
              {submitted && !editClosed && (
                <div className="text-sm pt-1 opacity-70">ยื่นสมัครแล้ว — แฟ้มถูกล็อกการแก้ไข เปลี่ยนสถานะเพื่อกลับมาแก้</div>
              )}
              {unused && !editClosed && (
                <div className="text-sm pt-1 opacity-70">ไม่ใช้แล้ว — แฟ้มถูกล็อกการแก้ไข เปลี่ยนสถานะเพื่อกลับมาแก้</div>
              )}
            </div>
            {statusChips(folio)}
            {citizen_id_verified && (
              <>
                <div className="bg-folio/50 h-[1px]" />
                <div className="px-4 md:px-6 py-2 flex items-center gap-2 text-base font-bold justify-between">
                  <a
                    className={cn('s-flex gap-1 text-folio', hasPdf ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed')}
                    role="button" tabIndex={0} aria-disabled={!hasPdf}
                    title={hasPdf ? undefined : noPdfHint}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                    onClick={() => (hasPdf ? _onModalOpen(folio, 'download') : showErrorToast(noPdfHint))}>
                    <IconDownload />
                    ดาวน์โหลด PDF
                  </a>
                </div>
              </>
            )}
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={folio._id}>
        <div className={cn('relative border mb-4 border-t-[3px] bg-folio_bg rounded-md border-folio _heading leading-tight', { 'opacity-40': unused })}>
          {!unused && favoriteStar(folio)}
          {/* โลโก้ ม. float ใต้ดาว — เหมือน .u-logo ของ FavoriteItem (margin-top เคลียร์โซนไอคอนมุม) */}
          <div className="float-right pt-9 pr-4 md:pt-10 md:pr-6">
            <img
              src={`https://assets.mytcas.com/i/logo/${course.university_id}.png`}
              alt="University Logo"
              width={44}
              height={44}
              className="size-11 sm:size-16 md:size-20"
            />
          </div>
          <div className="p-4 md:p-6">
            {/* ล็อกแก้ (ปิดรับ/สถานะ) ยังเข้าได้ — builder เปิดโหมดดูอย่างเดียว (เห็นเนื้อหาก่อนตัดสินใจทำสำเนา, 2026-09-14) */}
            <Link to={`/profile/folios/${folio._id}`}>
              <strong className="sm:text-xl">{_.get(course, 'university_name_th')} ➔</strong>
            </Link>
            <div className="text-base sm:text-lg">
              <div>{removeIfExists(_.get(course, 'campus_name_th'), 'วิทยาเขต')}</div>
              <div>{removeIfExists(_.get(course, 'faculty_name_th'), 'คณะ')}</div>
              <div>{removeIfExists(_.get(course, 'field_name_th'), 'สาขา')}</div>
              <div>{removeIfExists(_.get(course, 'program_name_th'), 'หลักสูตร')}</div>
              {majorName && majorName !== '0' ? (
                <div>
                  {!majorName.startsWith('วิชาเอก') ? 'วิชาเอก' : ''}
                  {majorName}
                </div>
              ) : null}
              {/* ~15% ของ catalog ไม่มีโครงการ (ไม่มี key project_name_th) — ไม่โชว์ป้าย "โครงการ" โดด ๆ (แบบเดียวกับ major) */}
              {projectName ? (
                <div className="text-sm pt-2">
                  {!projectName.startsWith('โครงการ') ? 'โครงการ' : ''}
                  {projectName}
                </div>
              ) : null}
              {/* row เดียว: อัปเดตล่าสุดชิดซ้าย (จาง) · ปิดรับสมัครชิดขวา (หนา) — จอแคบ wrap */}
              <div className="text-sm pt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <span className="opacity-50">อัปเดตล่าสุดเมื่อ {lastTimestamp}</span>
                {Boolean(_.get(course, 'folio.closed_date')) && (
                  <span className="font-bold">
                    ปิดรับสมัคร {formatFolioClosedDate(course.folio.closed_date)}
                    {Boolean(course.folio.closed_edit_date) && course.folio.closed_edit_date !== course.folio.closed_date && (
                      <> · แก้ไขแฟ้มได้ถึง {formatFolioClosedDate(course.folio.closed_edit_date)}</>
                    )}
                  </span>
                )}
              </div>
              {editClosed && (
                <div className="text-sm pt-1 text-[#A13333]">หลักสูตรนี้ปิดรับสมัครแล้ว</div>
              )}
              {submitted && !editClosed && (
                <div className="text-sm pt-1 opacity-70">ยื่นสมัครแล้ว — แฟ้มถูกล็อกการแก้ไข เปลี่ยนสถานะเพื่อกลับมาแก้</div>
              )}
              {unused && !editClosed && (
                <div className="text-sm pt-1 opacity-70">ไม่ใช้แล้ว — แฟ้มถูกล็อกการแก้ไข เปลี่ยนสถานะเพื่อกลับมาแก้</div>
              )}
            </div>
          </div>
          {/* ทำสำเนาได้แม้ล็อกแก้ — สำเนาเป็นแฟ้มใหม่ของหลักสูตรอื่น (server เช็คเฉพาะปลายทาง) */}
          {statusChips(folio, true)}
          {Boolean(citizen_id_verified) && (
            <>
              <div className="bg-folio/50 h-[1px]" />
              <div className="px-4 md:px-6 py-2 flex items-center gap-2 text-base font-bold justify-between">
                <a
                  className={cn('s-flex gap-1 text-folio', hasPdf ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed')}
                  role="button" tabIndex={0} aria-disabled={!hasPdf}
                  title={hasPdf ? undefined : noPdfHint}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                  onClick={() => (hasPdf ? _onModalOpen(folio, 'download') : showErrorToast(noPdfHint))}>
                  <IconDownload />
                  ดาวน์โหลด PDF
                </a>
                {/* ไม่ใช้ disabled — ต้องรับคลิกเพื่อเด้ง toast บอกเหตุผล (tooltip ใช้ไม่ได้บนมือถือ) */}
                <button
                  className={cn('btn-blank font-bold text-base s-flex gap-1 text-folio', { 'opacity-40 cursor-not-allowed': !hasPdf })}
                  aria-disabled={!hasPdf}
                  title={hasPdf ? undefined : noPdfHint}
                  onClick={() => (hasPdf ? _onModalOpen(folio, 'copy') : showErrorToast(noPdfHint))}>
                  <IconCopyLink />คัดลอกลิงก์
                </button>
              </div>
            </>
          )}
        </div>
        {(index < list.length - 1) && <hr />}
      </React.Fragment>
    );
  };

  return (
    <div className="t-box -folio">
      {(loading || downloading) && <div className='t-loading' />}
      <h2 className="title">แฟ้มสะสมผลงาน</h2>
      {activeFolios.map(renderFolio)}
      {/* ปุ่มกาง/ยุบ = หัว section กลุ่มไม่ใช้แล้ว — ตำแหน่งนิ่งทั้งยุบ/กาง ปุ่มซ่อนไม่หนีลงล่างสุด */}
      {unusedFolios.length > 0 && (
        <button
          type="button"
          // btn-blank บังคับ width: max-content !important — จัดกลางด้วย mx-auto (w-full โดนทับ)
          className="btn-blank mx-auto flex items-center gap-1 py-2 text-base font-bold text-[#999999] hover:text-folio transition-colors"
          aria-expanded={showUnused}
          onClick={() => setShowUnused((v) => !v)}>
          {`${showUnused ? 'ซ่อน' : 'แสดง'}พอร์ตที่ไม่ใช้แล้ว (${unusedFolios.length})`}
          {/* rotate-180 ของ tailwind ใช้ไม่ได้ — ไม่มี @tailwind base เลยไม่มี default ของ --tw-* vars ใน transform */}
          <IconChevronDown className="transition-transform" style={{ transform: showUnused ? 'rotate(180deg)' : 'none' }} />
        </button>
      )}
      {showUnused && unusedFolios.map(renderFolio)}
      {(folios || []).length < 40 && (
        <div>
          {(folios || []).length > 0 && <hr />}
          <h3 className="font-bold mb-0">สร้างแฟ้มสะสมผลงาน</h3>
          <p className="mt-2 text-sm leading-relaxed">
            สร้างแฟ้มสะสมผลงานผ่านระบบ TCASFolio เพื่อช่วยในการยื่นสมัครกับสถาบันต่างๆ
          </p>
          <Link to="/profile/folios/create" className="btn-main -full">
            <IconFolio />
            <span>สร้างแฟ้มสะสมผลงาน</span>
          </Link>
        </div>
      )}
      <ModalDownload
        visible={activeFolio && modalType === 'download'}
        submitting={downloading}
        onSubmit={() => _onDownload(activeFolio)}
        onClose={_onModalClose} />
      <ModalLinkCopy
        visible={activeFolio && modalType === 'copy'}
        submitting={copying}
        onSubmit={() => _onLinkCopy(activeFolio)}
        onClose={_onModalClose} />
    </div>
  )
}

export default SectionFolios;

const visibleStyles = {
  visibility: 'visible',
  opacity: 1,
  pointerEvents: 'auto'
};
const ModalDownload = ({ visible, submitting, onSubmit, onClose }) => {
  if (!visible) return null;
  return (
    <div className="t-modal" style={visibleStyles}>
      <div className="content">
        <a className="close cursor-pointer" onClick={onClose}>×</a>
        <h4>ขอให้ผู้สมัครตรวจสอบจำนวนหน้าของแฟ้มสะสมผลงานที่สร้างจากระบบว่าไม่เกินสูงสุดที่กำหนดโดยสถาบัน</h4>
        <p>
          <a className={cn("btn-main cursor-pointer", { disabled: submitting })} onClick={onSubmit}>
            <IconDownload />
            {submitting ? 'กำลังดาวน์โหลด... ' : 'ดาวน์โหลด PDF'}
          </a>
        </p>
      </div>
    </div>
  );
}

const ModalLinkCopy = ({ visible, submitting, onSubmit, onClose }) => {
  if (!visible) return null;
  return (
    <div className="t-modal" style={visibleStyles}>
      <div className="content">
        <a className="close cursor-pointer" onClick={onClose}>×</a>
        <h4>ขอให้ผู้สมัครตรวจสอบจำนวนหน้าของแฟ้มสะสมผลงานที่สร้างจากระบบว่าไม่เกินสูงสุดที่กำหนดโดยสถาบัน</h4>
        {/* ลิงก์ไม่ใช่สาธารณะ — api-pdf บังคับ accessToken (เจ้าของแฟ้ม/สถาบันเจ้าของหลักสูตร)
            เคส 08-31: เด็กเอาลิงก์ไปแปะที่ที่ไม่มี session แล้วโดนเด้งหน้าเข้าสู่ระบบ เข้าใจว่าคัดลอกไม่สำเร็จ */}
        <p className="text-left text-sm opacity-80">
          ลิงก์นี้เปิดได้เฉพาะผู้ที่เข้าสู่ระบบ mytcas — ตัวผู้สมัครเอง และสถาบันเจ้าของหลักสูตรที่ยื่นแฟ้มนี้
          <br />
          หากต้องการส่งแฟ้มให้ผู้อื่นดู ให้ใช้ปุ่ม “ดาวน์โหลด PDF” แล้วส่งไฟล์แทน
        </p>
        <p>
          <a className={cn("btn-main cursor-pointer", { disabled: submitting })} onClick={onSubmit}>
            {submitting ? 'กำลังคัดลอก...' : <><IconCopyLink />คัดลอกลิงก์</>}
          </a>
        </p>
      </div>
    </div>
  );
}
